import React, { useEffect, useState } from 'react'
import ReactEchartsCore from 'echarts-for-react/lib/core'
import echarts from 'echarts/lib/echarts'
import 'echarts/lib/chart/tree'
import 'echarts/lib/chart/line'
import 'echarts/lib/chart/lines'
import 'echarts/lib/chart/bar'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/dataZoom'
import 'echarts/lib/component/brush'
import 'echarts/lib/component/toolbox'
import 'echarts/lib/component/title'
import 'echarts/lib/component/legend'
import 'echarts/lib/component/graphic'
import { IBarChartProps } from '../utils'
import { useTheme } from '@mui/material'
import '../styles.css'
import moment from 'moment'
import { DEV_COLOR, PROD_COLOR, TEST_COLOR } from '../../../assets/jss/material-dashboard-react'
import { formatNumber } from '../../../utils/utils'

const BarChart = (props: IBarChartProps) => {
  const { name, metric, seriesData, frequency, size } = props

  const [series, setSeries] = useState<any>()
  const [leftMargin, setLeftMargin] = useState(40)
  const [noData, setNoData] = useState(false)

  const theme = useTheme()

  useEffect(() => {
    if (metric > 500 || (!metric && typeof metric != 'number')) {
      setLeftMargin(60)
    }
    if (size == 'xl' || size == 'lg') {
      setLeftMargin(100)
    }
    if (seriesData.length == 0) {
      setNoData(true)
    } else {
      setNoData(false)
    }
  }, [metric])

  useEffect(() => {
    if (Array.isArray(seriesData)) {
      if (seriesData.length == 0) {
        setNoData(true)
        setSeries({
          name: name,
          data: [],
          type: 'line',
        })
      } else {
        setNoData(false)
        setSeries([
          {
            name: name,
            data: seriesData,
            type: 'bar',
            barWidth: '60%',
            itemStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  {
                    offset: 0,
                    color: theme.palette.mode === 'dark' ? '#a3a3a3' : '#525252',
                  },
                  {
                    offset: 1,
                    color: theme.palette.mode === 'dark' ? '#737373' : '#404040',
                  },
                ],
              },
              borderRadius: [8, 8, 0, 0],
            },
            emphasis: {
              itemStyle: {
                color: theme.palette.mode === 'dark' ? '#d4d4d4' : '#737373',
              },
            },
          },
        ])
      }
    } else {
      let data = [] as any
      if (Object.keys(seriesData).length == 0) {
        setNoData(true)
        setSeries({
          name: name,
          data: [],
          type: 'line',
          symbol: 'circle',
          lineStyle: {
            width: 2.5,
          },
        })
      } else {
        setNoData(false)
        Object.keys(seriesData).forEach((key) => {
          const baseColor =
            key == 'dev' ? DEV_COLOR : key == 'test' ? TEST_COLOR : key == 'prod' ? PROD_COLOR : '#6366f1'
          data.push({
            name: key,
            data: seriesData[key],
            type: 'bar',
            stack: 'total',
            barWidth: '60%',
            itemStyle: {
              color: baseColor,
              borderRadius: key === 'prod' ? [8, 8, 0, 0] : 0,
            },
            emphasis: {
              focus: 'series',
              itemStyle: {
                shadowBlur: 10,
                shadowColor: 'rgba(0, 0, 0, 0.3)',
              },
            },
          })
        })
      }
      setSeries(data)
    }
  }, [seriesData])

  return (
    <ReactEchartsCore
      echarts={echarts}
      style={{ padding: 0, marginBottom: 0, height: '100%' }}
      option={{
        grid: {
          top: 30,
          bottom: 40,
          left: leftMargin,
          right: 30,
          height: '70%',
          containLabel: false,
        },
        graphic: {
          type: 'text',
          left: 'center',
          top: 'middle',
          invisible: !noData,
          style: {
            text: 'No data available',
            fill: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.5)',
            fontSize: 14,
            fontWeight: 500,
          },
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
            shadowStyle: {
              color: theme.palette.mode === 'dark' ? 'rgba(64, 64, 64, 0.2)' : 'rgba(0, 0, 0, 0.05)',
            },
          },
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(23, 23, 23, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(115, 115, 115, 0.3)' : 'rgba(0, 0, 0, 0.1)',
          borderWidth: 1,
          textStyle: {
            color: theme.palette.mode === 'dark' ? '#fff' : '#000',
            fontSize: 12,
          },
          padding: 12,
          formatter: (params) => {
            if (!params || !Array.isArray(params) || params.length === 0) return ''

            // Get the date from the first param
            const firstParam = params[0]
            let date = ''
            if (Array.isArray(firstParam.data)) {
              date = moment.utc(firstParam.data[0]).format('MM/DD')
            } else if (firstParam.axisValue) {
              date = moment.utc(firstParam.axisValue).format('MM/DD')
            }

            let tooltipContent = `${date}<br />`

            // Build tooltip dynamically based on available series (prod, test, dev order)
            ;[...params].reverse().forEach((param) => {
              let value = Array.isArray(param.data) ? param.data[1] : param.value
              if (value == null) {
                value = '-'
              } else if (typeof value === 'number') {
                if (name == 'Cost') {
                  value = '$' + formatNumber(value.toFixed(2))
                } else {
                  value = formatNumber(value.toFixed(2))
                }
              }
              const label = param.seriesName.charAt(0).toUpperCase() + param.seriesName.slice(1)
              tooltipContent += `${param.marker}${label}: ${value}<br />`
            })

            return tooltipContent
          },
        },
        xAxis: {
          type: 'category',
          splitLine: {
            show: false,
          },
          axisLabel: {
            color: theme.palette.mode == 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            fontSize: 11,
            fontWeight: 500,
            formatter: function (value: string) {
              if (frequency == 'hourly') {
                return moment(value).format('HH:mm')
              } else {
                return moment.utc(value).format('MM/DD')
              }
            },
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: theme.palette.mode == 'dark' ? 'rgba(64, 64, 64, 0.4)' : 'rgba(0, 0, 0, 0.1)',
              width: 1,
            },
          },
          axisTick: {
            show: false,
          },
        },
        yAxis: {
          type: 'value',
          splitLine: {
            show: true,
            lineStyle: {
              color: theme.palette.mode == 'dark' ? 'rgba(64, 64, 64, 0.2)' : 'rgba(0, 0, 0, 0.06)',
              type: 'dashed',
            },
          },
          axisLabel: {
            color: theme.palette.mode == 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            fontSize: 11,
            fontWeight: 500,
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
        series: series,
      }}
    />
  )
}

export default BarChart
